/**
 * rolePageAccess.js — who may see a role page, and where its buttons go.
 * Shaped by prompt 38; MKT-01, MKT-11, MKT-20, MKT-23, MKT-25. D-38-01, D-38-03.
 * Invariants: published stays false in source; a role page never grants access;
 * unpublished content is hidden unless the viewer is a platform admin or a
 * dev host with ?preview=1. Sign-in-only pages show a staff sign-in screen
 * to signed-out visitors and content only to a platform admin.
 * Related: rolePageCopyData.js, RolePageRoute.jsx, fusionMsiMarketingRouting.js.
 */

import { isDevAuthEnvironment } from 'config/devAuthCodes';
import {
  aboutPath,
  audienceSignupPath,
  authPath,
  businessModelPath,
  historicalPrecedentPath,
  isFusionMsiHost,
  platformPagePath,
} from './fusionMsiMarketingRouting';

/** Test-only. Never read from the URL. Cleared after each test. */
let publishedOverride = null;

export function setRolePagePublishedOverrideForTests(map) {
  publishedOverride = map;
}

export function clearRolePagePublishedOverrideForTests() {
  publishedOverride = null;
}

export function isRolePagePublished(page) {
  if (!page) return false;
  if (publishedOverride && Object.prototype.hasOwnProperty.call(publishedOverride, page.slug)) {
    return publishedOverride[page.slug] === true;
  }
  return page.published === true;
}

/**
 * Dev-host preview needs ?preview=1. A bare localhost visit stays unpublished
 * so signed-out click-throughs still see not-found (D-38-01).
 */
export function canPreviewUnpublishedRolePage({ isGlobalAdmin, previewQuery }) {
  if (isGlobalAdmin) return true;
  return Boolean(previewQuery) && isDevAuthEnvironment();
}

/**
 * @returns {'not-found' | 'staff-sign-in' | 'preview' | 'content'}
 */
export function rolePageView({ page, signedIn, isGlobalAdmin, previewQuery }) {
  if (!page || page.kind === 'redirect') return 'not-found';

  const published = isRolePagePublished(page);
  const preview = !published && canPreviewUnpublishedRolePage({ isGlobalAdmin, previewQuery });

  if (page.visibility === 'signInOnly') {
    if (!signedIn) return 'staff-sign-in';
    // Finance roles are not built. Both staff pages require the admin claim.
    if (!isGlobalAdmin) return 'not-found';
    if (!published && !preview) return 'not-found';
    return published ? 'content' : 'preview';
  }

  if (!published && !preview) return 'not-found';
  return published ? 'content' : 'preview';
}

export function rolePageNeedsNoindex(page, view) {
  if (!page) return false;
  if (page.visibility === 'unlisted' || page.visibility === 'signInOnly') return true;
  return view === 'preview';
}

export function rolePagePath(slug) {
  if (!slug) return isFusionMsiHost() ? '/' : '/dp';
  return isFusionMsiHost() ? `/for/${slug}` : `/dp/for/${slug}`;
}

/**
 * @returns {{ kind: 'link', href: string } | { kind: 'scroll', target: string } | null}
 */
export function resolveRoleCta(cta, slug) {
  if (!cta || cta.action === 'none') return null;
  if (cta.action === 'scroll') {
    return { kind: 'scroll', target: cta.target || 'role-cards' };
  }
  if (cta.action === 'contact') {
    const topic = cta.topic || slug || '';
    const base = platformPagePath('get-started');
    const href = topic ? `${base}?topic=${encodeURIComponent(topic)}` : base;
    return { kind: 'link', href };
  }
  if (cta.action === 'signIn') return { kind: 'link', href: authPath() };
  if (cta.action === 'findCare') return { kind: 'link', href: '/caremap2' };
  if (cta.action === 'createMasterChart') return { kind: 'link', href: audienceSignupPath('patient') };
  if (cta.action === 'role' && cta.slug) return { kind: 'link', href: rolePagePath(cta.slug) };
  return null;
}

export function roleFooterHref(key) {
  if (key === 'business-model') return businessModelPath();
  if (key === 'historical-precedent') return historicalPrecedentPath();
  if (key === 'about') return aboutPath();
  if (key === 'terms') return '/terms-of-use';
  if (key === 'privacy') return '/privacy-policy';
  return null;
}

export const ROLE_FOOTER_LINK_LABELS = {
  'business-model': 'Business model',
  'historical-precedent': 'Historical precedent',
  about: 'About',
  terms: 'Terms of Use',
  privacy: 'Privacy Policy',
};
